<template>
  <div qaid="dropShipOrdersContainer">
    <!-- MiniCart Header -->
    <div v-if="isMiniCart" class="flex justify-between">
      <heading id="miniCart__callout--empty" class="mb-3" :level="1" size="sm" qaid="dropShipOrdersContainer--header">
        {{ bp('yourContainers') }}
        <span class="text-md text-secondary ml-1">{{ fullCountText }}</span>
      </heading>
      <div v-if="volumeHTML" class="text-md text-secondary mb-3" v-html="volumeHTML" />
    </div>

    <!-- Cart Header -->
    <div v-else class="section-header section-header--dividing section-header--xl">
      <div class="flex items-end">
        <h1 class="heading heading--xl" qaid="dropShipOrdersContainer--header">
          {{ bp('yourContainers') }}
        </h1>
        <span class="heading--xl text-md text-secondary ml-3">{{ fullCountText }}</span>
        <span v-if="volumeHTML" class="flex content-end heading--xl text-md text-secondary ml-2" v-html="volumeHTML" />
        <i v-tippy="warningTippyConfig" class="material-icons md-18 text-black ml-25 pt-px cursor-pointer">info</i>
      </div>

      <!-- Calculation Warning tooltip -->
      <div id="warning-tooltip" v-tippy-html qaid="warningTooltip" class="text-left">
        {{ bp('containerDimension') }}
      </div>
    </div>

    <!-- Containers -->
    <template v-if="isMiniCart">
      <!-- Mini Cart Paginated containers -->
      <div class="flex flex-wrap" qaid="dropShipOrdersContainer--containers">
        <ContainerIndicator
          v-for="(indicator, index) in paginatedData"
          :key="index"
          class="mr-1 mb-2"
          :container="indicator.container"
          :containers="containers"
          :is-add-button="indicator.isAddButton"
          :is-limit-warning="indicator.isLimitWarning"
          :base-properties="baseProperties"
          :show-delete-button="true"
          qaid="dropShipOrdersContainer--containerIndicator"
          @add-container="$emit('add-container')"
        />
      </div>

      <div v-if="pageCount > 1" class="flex justify-center mb-3">
        <pagination
          ref="pagination"
          :page-number="pageNumber"
          :page-count="pageCount"
          @page-change="pageChange"
          @prev-page="prevPage"
          @next-page="nextPage"
        />
      </div>
    </template>
    <template v-else>
      <!-- Cart non-paginated containers -->
      <div class="flex flex-wrap mb-3" qaid="dropShipOrdersContainer--containers">
        <ContainerIndicator
          v-for="(indicator, index) in listData"
          :key="`${index}-${indicator.container ? indicator.container.id : ''}`"
          class="mr-2 mb-2"
          :container="indicator.container"
          :containers="containers"
          :is-add-button="indicator.isAddButton"
          :is-limit-warning="indicator.isLimitWarning"
          :base-properties="baseProperties"
          :show-tooltip-config="true"
          :show-delete-button="true"
          qaid="dropShipOrdersContainer--containerIndicator"
        />
      </div>
    </template>

    <a v-if="isCheckout && !isPrint" :href="`${encodedContextPath}/cart`" class="underline">{{
      bp('containerAdjust')
    }}</a>

    <Callout v-if="!isMiniCart && containers && containers.vendorNotificationText" class="mt-3" type="warning">
      {{ containers.vendorNotificationText }}
    </Callout>
  </div>
</template>

<script>
import pagination from '../../../mixins/pagination';
import {mapState} from 'vuex';
import Heading from '@/components/Heading';
import ContainerIndicator from '@/components/containers/drop-ship/ContainerIndicator';
import Pagination from '@/components/Pagination';
import Callout from '@/components/Callout';

export default {
  components: {Heading, ContainerIndicator, Pagination, Callout},

  mixins: [pagination],

  props: {
    baseProperties: {
      type: Array,
      required: false,
      default: () => [],
    },
    containers: {
      type: Object,
      required: false,
      default: () => {},
    },
    isMiniCart: Boolean,
    isCheckout: Boolean,
    isPrint: Boolean,
    size: {
      type: Number,
      required: false,
      default: 4,
    },
  },

  data() {
    return {
      maxContainers: 15,
      warningTippyConfig: {
        html: '#warning-tooltip',
        theme: 'light',
        trigger: 'mouseenter',
        maxWidth: '20rem',
        reactive: true,
      },
    };
  },

  computed: {
    listData() {
      let result = [];

      if (this.containers) {
        if (this.containers.fortyHiFeetContainers) result = result.concat(this.containers.fortyHiFeetContainers);
        if (this.containers.fortyFeetContainers) result = result.concat(this.containers.fortyFeetContainers);
        if (this.containers.twentyFeetContainers) result = result.concat(this.containers.twentyFeetContainers);

        const containerCount = result.length;

        result = result.map((container) => {
          return {container: container, isAddButton: false, isLimitWarning: false};
        });

        if (!this.isCheckout && containerCount < this.maxContainers) {
          result.push({container: null, isAddButton: true, isLimitWarning: false});
        } else if (!this.isCheckout && containerCount >= this.maxContainers) {
          result.push({container: null, isAddButton: false, isLimitWarning: true});
        }
      }

      return result;
    },

    fullCountText() {
      if (this.containers) {
        return this.bp('containerFullCount')
          .replace('{0}', this.containers.totalContainersFull)
          .replace('{1}', this.containers.totalContainers);
      }

      return null;
    },

    volumeHTML() {
      if (this.containers && this.containers.consumedVolume && this.containers.totalVolume) {
        return this.bp('containerVolumeCount')
          .replace('{0}', parseFloat(Math.ceil(this.containers.consumedVolume * 100) / 100).toFixed(2))
          .replace('{1}', parseFloat(Math.floor(this.containers.totalVolume * 100) / 100).toFixed(2));
      }

      return null;
    },

    ...mapState('backend', ['encodedContextPath']),
  },
};
</script>
